<template>
  <dx-data-grid
      class="grid collapsable"
      :style="`width: 100%; height: 100%; max-height:${tableHeight}`"
      :data-source="vesselsDataSource"
      :remote-operations="false"
      :show-column-lines="true"
      :show-row-lines="true"
      :show-borders="true"
      :word-wrap-enabled="true"
      :row-alternation-enabled="false"
      :column-auto-width="true"
      @selection-changed="vesselSelected"
      @content-ready="contentReady"
      @editor-preparing="overrideOnValueChanged"
  >
    <DxSelection mode="single"/>
    <DxHeaderFilter :visible="true"/>
    <DxFilterRow :visible="false"/>
    <DxLoadPanel :enabled="false"/>
    <DxPaging :page-size="10"/>
    <dx-column
        :allowHeaderFiltering="true"
        :filter-operations="['contains']"
        alignment="left"
        data-field="vessel"
        caption="Vessel"
    >
    </dx-column>
    <dx-column
        :allowHeaderFiltering="true"
        :filter-operations="['contains']"
        alignment="left"
        data-field="eng_type"
        caption="Engine"
        :allowSorting="false"
    >
    </dx-column>
    <DxMasterDetail
        :enabled="false"
        template="faultsTemplate"
    />

    <template #faultsTemplate="{}">
      <dx-data-grid
          class="grid collapsable"
          :style="`width: 100%; height: 100%; max-height:${faultTableHeight}`"
          :data-source="faultsDataSource"
          :remote-operations="false"
          :show-column-lines="true"
          :show-row-lines="true"
          :show-borders="false"
          :word-wrap-enabled="true"
          :row-alternation-enabled="false"
          :column-auto-width="true"
          @selection-changed="faultSelected"
          @content-ready="contentReady"
          @editor-preparing="overrideOnValueChanged"
      >
        <DxSelection mode="single"/>
        <DxHeaderFilter :visible="true"/>
        <DxFilterRow :visible="false"/>
        <DxLoadPanel :enabled="false"/>
        <DxPaging :page-size="15"/>
        <dx-column
            :allowHeaderFiltering="true"
            :filter-operations="['contains']"
            alignment="left"
            data-field="description"
            caption="Fault"
        />
        <DxMasterDetail
            :enabled="false"
            template="componentsTemplate"
        />
        <template #componentsTemplate="{}">
          <dx-data-grid
              class="grid collapsable"
              :style="`width: 100%; height: 100%; max-height:${componentTableHeight}`"
              :data-source="componentsDataSource"
              :remote-operations="false"
              :show-column-lines="true"
              :show-row-lines="true"
              :show-borders="false"
              :word-wrap-enabled="true"
              :row-alternation-enabled="false"
              :column-auto-width="true"
              :showColumnHeaders="true"
              @selection-changed="componentSelected"
              @content-ready="contentReady"
              @editor-preparing="overrideOnValueChanged"
          >
            <DxSelection mode="single"/>
            <DxHeaderFilter :visible="true"/>
            <DxFilterRow :visible="false"/>
            <DxLoadPanel :enabled="false"/>
            <DxPaging :page-size="15"/>
            <dx-column
                :allowHeaderFiltering="true"
                :filter-operations="['contains']"
                alignment="left"
                data-field="component"
                caption="Component"
            />
            <dx-column
                v-if="kpis"
                width="100"
                :allowHeaderFiltering="false"
                :filter-operations="['contains']"
                alignment="left"
                data-field="kpi"
                caption="KPI"
            />
            <DxMasterDetail
                :enabled="false"
                template="instancesTemplate"
            />
            <template #instancesTemplate="{}">
              <dx-data-grid
                  class="grid collapsable"
                  :style="`width: 100%; height: 100%; max-height:${instancesTableHeight}`"
                  :data-source="instancesDataSource"
                  :remote-operations="true"
                  :show-column-lines="true"
                  :show-row-lines="true"
                  :show-borders="true"
                  :word-wrap-enabled="true"
                  :row-alternation-enabled="false"
                  :column-auto-width="true"
                  @editor-preparing="overrideOnValueChanged"
              >
                <DxFilterRow :visible="true"/>
                <DxLoadPanel :enabled="false"/>
                <DxPaging :page-size="5"/>
                <dx-column
                    :allowHeaderFiltering="false"
                    :filter-operations="['=', '>=', '>', '<', '<=', 'between']"
                    alignment="left"
                    data-field="first"
                    caption="First Appearance"
                    data-type="datetime"
                    format="yyyy/MM/dd HH:mm"
                >
                </dx-column>
                <dx-column
                    :allowHeaderFiltering="false"
                    :allowFiltering="false"
                    :filter-operations="[]"
                    alignment="left"
                    data-field="verified"
                    data-type="boolean"
                    caption="Status"
                    cell-template="verifiedTemp"
                    :minWidth="'150px'"
                    :width="'150px'"
                    :maxWidth="'150px'"
                    :allowSorting="false"
                />
                <div slot="verifiedTemp" slot-scope="{data}">
                  <ColorRadioV2
                      :id="data.row.data.id"
                      :selected="data.row.data.verified"
                      :items="status_items"
                      :callback="selectStatus"
                      :noChange="!updateAllowed"
                  />
                </div>
              </dx-data-grid>
            </template>
          </dx-data-grid>
        </template>
      </dx-data-grid>
    </template>
  </dx-data-grid>
</template>

<script>
import {DxColumn, DxDataGrid, DxFilterRow, DxHeaderFilter, DxLoadPanel, DxMasterDetail, DxPaging, DxSelection} from "devextreme-vue/data-grid";
import ColorRadioV2 from "@/components/ColorRadioV2";
import {store} from "@/main";
import {createStore} from "devextreme-aspnet-data-nojquery";

export default {
  name: "FaultsCollapsableTableWithHeaderFilters",
  components: {
    DxDataGrid,
    DxLoadPanel,
    DxColumn,
    DxPaging,
    DxFilterRow,
    DxHeaderFilter,
    DxMasterDetail,
    DxSelection,
    ColorRadioV2
  },
  props: {
    kpis: Boolean,
    verified: Boolean,
    pendingUpdatesFlag: Boolean,
    status_items: Object
  },
  data() {
    return {
      vesselsDataSource: null,
      faultsDataSource: null,
      componentsDataSource: null,
      instancesDataSource: null,
      instancesDataSourceSimplified: null,
      tableHeight: '0px',
      faultTableHeight: '0px',
      componentTableHeight: '0px',
      instancesTableHeight: '0px',
      selectedVesselRow: null,
      selectedFaultRow: null,
      selectedComponentRow: null,
      pending_updates: {}
    }
  },
  methods: {
    getPeriod() {
      let period;

      switch (store.type) {
        case 'month':
          period = '1month'
          break;
        case 'threemonths':
          period = '3months'
          break;
        case 'sixmonths':
          period = '6months'
          break;
        case 'twelvemonths':
          period = '12months'
          break;
      }

      return period;
    },
    clearPending() {
      this.pending_updates = {};
      this.pendingUpdatesFlag = false;
      this.faultUpdateSync()
    },
    setVesselsDataSource: function () {
      let period = this.getPeriod(),
          params = {
            "verified": !!this.verified
          };

      this.vesselsDataSource = createStore({
        loadUrl: `${store.config.api_location}/faults/groups/period/${period}`,
        loadMethod: "POST",
        loadParams: params,
        onLoading: (options) => {
          this.faultsDataSourceOptions = options;
          this.clearPending()
          store.loading = true;
        },
        onLoaded: (data) => {
          store.loading = false;
        },
        onAjaxError: (e) => {
          store.loading = false;
        }
      });
    },
    setFaultsDataSource: function (vessel) {
      let period = this.getPeriod(),
          params = {
            "imo": vessel.imo,
            "verified": !!this.verified
          };

      this.faultsDataSource = createStore({
        loadUrl: `${store.config.api_location}/faults/groups/period/${period}`,
        loadMethod: "POST",
        loadParams: params,
        onLoading: () => {
          store.loading = true;
          this.clearPending();
        },
        onLoaded: (data) => {
          store.loading = false;
        },
        onAjaxError: () => {
          store.loading = false;
        }
      });
    },
    setComponentsDataSource: function (fault) {
      let period = this.getPeriod(),
          params = {
            "imo": fault.imo,
            "fault": fault.description,
            "verified": !!this.verified
          };

      this.componentsDataSource = createStore({
        loadUrl: `${store.config.api_location}/faults/groups/period/${period}`,
        loadMethod: "POST",
        loadParams: params,
        onLoading: (options) => {
          store.loading = true;
          this.clearPending();
        },
        onLoaded: (data) => {
          store.loading = false;
        },
        onAjaxError: (e) => {
          store.loading = false;
        }
      });
    },
    setInstancesDataSource: function (component) {
      let period = this.getPeriod(),
          params = {
            "imo": component.imo,
            "description": component.description,
            "component": component.component,
            "pending": JSON.stringify(this.pending_updates),
            "verified": !!this.verified
          };

      this.instancesDataSource = createStore({
        loadUrl: `${store.config.api_location}/faults/groups/instances/period/${period}`,
        loadMethod: "POST",
        loadParams: params,
        onLoading: (options) => {
          store.loading = true;
        },
        onLoaded: (data) => {
          store.loading = false;
          this.instancesDataSourceSimplified = data;
        },
        onAjaxError: (e) => {
          store.loading = false;
        }
      });
    },
    vesselSelected: function (e) {
      e.component.collapseAll(-1);
      this.selectedVesselRow = e.currentSelectedRowKeys[0];

      e.component.expandRow(this.selectedVesselRow);
      this.setFaultsDataSource(this.selectedVesselRow);
    },
    faultSelected: function (e) {
      e.component.collapseAll(-1);
      this.selectedFaultRow = e.currentSelectedRowKeys[0];

      if (!!this.selectedFaultRow) {
        e.component.expandRow(this.selectedFaultRow);
        this.setComponentsDataSource(this.selectedFaultRow);
      }
    },
    componentSelected: function (e) {
      e.component.collapseAll(-1);
      this.selectedComponentRow = e.currentSelectedRowKeys[0];

      if (!!this.selectedComponentRow) {
        e.component.expandRow(this.selectedComponentRow);
        this.setInstancesDataSource(this.selectedComponentRow);
      }
    },
    contentReady: function (e) {
      let selected = e.component.getSelectedRowKeys(),
          lookup = e.component.getVisibleRows().filter((row) => {
            return row.data == selected[0]
          });

      if (!e.component.getSelectedRowKeys().length || !lookup.length) {
        e.component.selectRowsByIndexes(0);
      }
    },
    calculateTableHeight: function () {
      let height = (window.outerHeight / 12 * 8).toFixed(0);
      this.tableHeight = height + 'px !important';
      this.faultTableHeight = (height * 0.8) + 'px';
      this.componentTableHeight = (height * 0.6) + 'px';
      this.instancesTableHeight = (height * 0.4) + 'px';
    },
    overrideOnValueChanged: function (e) {
      if (e.parentType == 'filterRow') {
        e.editorOptions.placeholder = `Filter: ${e.caption}`;
      }
    },
    selectStatus: function (id, value) {
      let update_performed = false,
          current_pending = Object.keys(this.pending_updates).length;

      if (value >= 0) {
        let source = this.instancesDataSourceSimplified.filter((item) => {
          return item.id == id;
        })[0];

        if (Object.keys(this.pending_updates).indexOf(id) < 0 || source.original != value) {
          update_performed = true;
          this.pending_updates[id] = {
            'verified': value
          }
        }
      }

      if (!update_performed) {
        delete this.pending_updates[id];
      }

      if (current_pending != Object.keys(this.pending_updates).length || update_performed) {
        this.pendingUpdatesFlag = Object.keys(this.pending_updates).length > 0;
        this.faultUpdateSync()
        this.setInstancesDataSource(this.selectedComponentRow)
      }
    },
    async faultUpdateSync() {
      this.$emit('update:pendingUpdatesFlag', this.pendingUpdatesFlag);
    },
    reloadInstancesSource: function () {
      this.pending_updates = {};
      this.pendingUpdatesFlag = false;
      this.setInstancesDataSource(this.selectedComponentRow);
    }
  },
  mounted() {
    if (!!this.verified) {
      this.status_items = this.status_items.filter((item) => {
        return item.value > -2
      })
    }

    this.setVesselsDataSource();
    this.calculateTableHeight();
  },
  computed: {
    period: function () {
      return store.type;
    },
    updateAllowed: () => {
      let admin = store.userProfile.roles.filter((item) => {
        return item.toLowerCase().indexOf('admin') > -1
      }).length > 0;

      return admin
    }
  },
  watch: {
    period: function () {
      this.setVesselsDataSource();
    },
    pendingUpdatesFlag: {
      handler () {
        this.faultUpdateSync()
      },
      immediate: true,
      deep: true
    }
  }
}
</script>

<style>
  .grid.collapsable .dx-master-detail-cell {
    padding: 0 !important;
  }

  .grid.collapsable .dx-master-detail-cell {
    padding-left: 32px !important;
    border-bottom: none !important;
  }
</style>